import type {
  CustomersTodayReport,
  ExtraReport,
  MenuBranding,
  MenuSettings,
  ExtraReportMeta,
  CustomerDetail,
  CustomerListReport,
  BalanceReport,
  BranchOption,
  DashboardKpis,
  DeliveryReport,
  DeviceOption,
  InventoryReport,
  ProductSalesReport,
  ProductTrendReport,
  LedgerReport,
  OrderDetail,
  OrderListRow,
  Paged,
  PurchaseReport,
  PurchaseReturnReport,
  ReportRange,
  SalesReport,
  SalesReturnReport,
  ShiftReportRow,
  SyncStatusSummary,
  TenantProfile,
} from '@order-pilot/shared';
import { API_URL, portalApi, queryString } from '@/lib/api';
import { tenantSession } from '@/lib/session';

export interface OrderListParams extends ReportRange {
  page: number;
  pageSize: number;
  q: string;
  /** The Filters popover's rows, already serialised as the JSON the API expects. */
  filters?: string;
  /** 'true' → OR the filter rows; omitted/anything else → AND them. */
  matchAny?: string;
}

/**
 * Every portal key starts with `portal`, which is also the prefix the tenant
 * auth context drops on sign-out — so a logout cannot leave a report cached.
 */
export const portalKeys = {
  all: ['portal'] as const,
  me: () => ['portal', 'me'] as const,
  branches: () => ['portal', 'branches'] as const,
  devices: () => ['portal', 'devices'] as const,
  syncStatus: () => ['portal', 'sync-status'] as const,
  dashboard: (range: ReportRange) => ['portal', 'reports', 'dashboard', range] as const,
  sales: (range: ReportRange) => ['portal', 'reports', 'sales', range] as const,
  shifts: (range: ReportRange) => ['portal', 'reports', 'shifts', range] as const,
  ledger: (range: ReportRange) => ['portal', 'reports', 'ledger', range] as const,
  receivables: (range: ReportRange) => ['portal', 'reports', 'receivables', range] as const,
  payables: (range: ReportRange) => ['portal', 'reports', 'payables', range] as const,
  deliveries: (range: ReportRange) => ['portal', 'reports', 'deliveries', range] as const,
  salesReturns: (range: ReportRange) =>
    ['portal', 'reports', 'sales-returns', range] as const,
  purchases: (range: ReportRange) => ['portal', 'reports', 'purchases', range] as const,
  inventory: (range: ReportRange) => ['portal', 'reports', 'inventory', range] as const,
  productSales: (range: ReportRange) =>
    ['portal', 'reports', 'products', range] as const,
  productTrend: (range: ReportRange, product: string) =>
    ['portal', 'reports', 'products', 'trend', range, product] as const,
  // Deliberately not range-keyed: "what has this person ever bought" is a
  // lifetime question, and a date filter would quietly hide their history.
  customers: () => ['portal', 'customers'] as const,
  customersToday: (date: string) => ['portal', 'customers', 'today', date] as const,
  customer: (key: string) => ['portal', 'customers', key] as const,
  extraReports: () => ['portal', 'extra-reports'] as const,
  menuSettings: () => ['portal', 'menu-settings'] as const,
  extraReport: (key: string, range: ReportRange) =>
    ['portal', 'reports', 'extra', key, range] as const,
  purchaseReturns: (range: ReportRange) =>
    ['portal', 'reports', 'purchase-returns', range] as const,
  orders: (params: OrderListParams) => ['portal', 'orders', 'list', params] as const,
  order: (id: string) => ['portal', 'orders', 'detail', id] as const,
};

function rangeQuery(range: ReportRange): string {
  return queryString({
    from: range.from,
    to: range.to,
    branchId: range.branchId,
    deviceId: range.deviceId,
  });
}

/** The tenant's smart-menu settings. */
export function fetchMenuSettings(signal?: AbortSignal): Promise<MenuSettings> {
  return portalApi.get<MenuSettings>('/portal/menu', { signal });
}

export function updateMenuBranding(change: Partial<MenuBranding>): Promise<MenuSettings> {
  return portalApi.patch<MenuSettings>('/portal/menu/branding', change);
}

export function updateMenuBranch(
  branchId: string,
  change: { enabled?: boolean; hidden?: string[] },
): Promise<MenuSettings> {
  return portalApi.patch<MenuSettings>(
    `/portal/menu/branches/${encodeURIComponent(branchId)}`,
    change,
  );
}

/** The data-driven report menu. */
export function fetchExtraReports(signal?: AbortSignal): Promise<ExtraReportMeta[]> {
  return portalApi.get<ExtraReportMeta[]>('/portal/extra-reports', { signal });
}

/** One data-driven report. The undated ones ignore the range on the server. */
export function fetchExtraReport(
  key: string,
  range: ReportRange,
  signal?: AbortSignal,
): Promise<ExtraReport> {
  return portalApi.get<ExtraReport>(
    `/portal/extra-reports/${encodeURIComponent(key)}${rangeQuery(range)}`,
    { signal },
  );
}

export function fetchProfile(signal?: AbortSignal): Promise<TenantProfile> {
  return portalApi.get<TenantProfile>('/portal/me', { signal });
}

export function fetchBranches(signal?: AbortSignal): Promise<BranchOption[]> {
  return portalApi.get<BranchOption[]>('/portal/branches', { signal });
}

export function fetchDevices(signal?: AbortSignal): Promise<DeviceOption[]> {
  return portalApi.get<DeviceOption[]>('/portal/devices', { signal });
}

export function fetchSalesReturns(
  range: ReportRange,
  signal?: AbortSignal,
): Promise<SalesReturnReport> {
  return portalApi.get<SalesReturnReport>(
    `/portal/reports/sales-returns${rangeQuery(range)}`,
    { signal },
  );
}

/**
 * Stock on hand. Takes the whole range for cache-key and querystring
 * consistency with the other reports, but the server applies only its
 * branch/till fields — inventory is a snapshot, not a range.
 */
export function fetchInventory(
  range: ReportRange,
  signal?: AbortSignal,
): Promise<InventoryReport> {
  return portalApi.get<InventoryReport>(
    `/portal/reports/inventory${rangeQuery(range)}`,
    { signal },
  );
}

export function fetchProductSales(
  range: ReportRange,
  signal?: AbortSignal,
): Promise<ProductSalesReport> {
  return portalApi.get<ProductSalesReport>(
    `/portal/reports/products${rangeQuery(range)}`,
    { signal },
  );
}

/**
 * One product's series. `product` is the sale-time NAME, which is how the
 * list groups — not a catalog id, so a renamed product still resolves.
 */
export function fetchProductTrend(
  range: ReportRange,
  product: string,
  signal?: AbortSignal,
): Promise<ProductTrendReport> {
  return portalApi.get<ProductTrendReport>(
    `/portal/reports/products/trend${rangeQuery(range)}&product=${encodeURIComponent(product)}`,
    { signal },
  );
}

export function fetchPurchases(
  range: ReportRange,
  signal?: AbortSignal,
): Promise<PurchaseReport> {
  return portalApi.get<PurchaseReport>(
    `/portal/reports/purchases${rangeQuery(range)}`,
    { signal },
  );
}

export function fetchPurchaseReturns(
  range: ReportRange,
  signal?: AbortSignal,
): Promise<PurchaseReturnReport> {
  return portalApi.get<PurchaseReturnReport>(
    `/portal/reports/purchase-returns${rangeQuery(range)}`,
    { signal },
  );
}

export function fetchSyncStatus(signal?: AbortSignal): Promise<SyncStatusSummary> {
  return portalApi.get<SyncStatusSummary>('/portal/sync-status', { signal });
}

export function fetchDashboard(range: ReportRange, signal?: AbortSignal): Promise<DashboardKpis> {
  return portalApi.get<DashboardKpis>(`/portal/reports/dashboard${rangeQuery(range)}`, { signal });
}

export function fetchSalesReport(range: ReportRange, signal?: AbortSignal): Promise<SalesReport> {
  return portalApi.get<SalesReport>(`/portal/reports/sales${rangeQuery(range)}`, { signal });
}

export function fetchShifts(range: ReportRange, signal?: AbortSignal): Promise<ShiftReportRow[]> {
  return portalApi.get<ShiftReportRow[]>(`/portal/reports/shifts${rangeQuery(range)}`, { signal });
}

export function fetchLedger(range: ReportRange, signal?: AbortSignal): Promise<LedgerReport> {
  return portalApi.get<LedgerReport>(`/portal/reports/ledger${rangeQuery(range)}`, { signal });
}

export function fetchReceivables(range: ReportRange, signal?: AbortSignal): Promise<BalanceReport> {
  return portalApi.get<BalanceReport>(`/portal/reports/receivables${rangeQuery(range)}`, { signal });
}

export function fetchPayables(range: ReportRange, signal?: AbortSignal): Promise<BalanceReport> {
  return portalApi.get<BalanceReport>(`/portal/reports/payables${rangeQuery(range)}`, { signal });
}

export function fetchDeliveries(range: ReportRange, signal?: AbortSignal): Promise<DeliveryReport> {
  return portalApi.get<DeliveryReport>(`/portal/reports/deliveries${rangeQuery(range)}`, { signal });
}

export function fetchOrders(params: OrderListParams, signal?: AbortSignal): Promise<Paged<OrderListRow>> {
  const query = queryString({
    from: params.from,
    to: params.to,
    branchId: params.branchId,
    page: params.page,
    pageSize: params.pageSize,
    q: params.q,
    filters: params.filters,
    matchAny: params.matchAny,
  });
  return portalApi.get<Paged<OrderListRow>>(`/portal/orders${query}`, { signal });
}

export function fetchOrder(id: string, signal?: AbortSignal): Promise<OrderDetail> {
  return portalApi.get<OrderDetail>(`/portal/orders/${encodeURIComponent(id)}`, { signal });
}

/** Reports the server can render as a file. Mirrors EXPORTABLE on the API. */
export type ExportableReport =
  | 'sales'
  | 'shifts'
  | 'ledger'
  | 'purchases'
  | 'deliveries'
  | 'receivables'
  | 'payables'
  | 'sales-returns'
  | 'purchase-returns'
  | 'inventory'
  | 'products';
export type ExportFormat = 'pdf' | 'xlsx';

/**
 * Fetch a generated report and save it.
 *
 * Deliberately NOT a plain `<a download>`: the portal authenticates with a
 * bearer token rather than a cookie, so a link would arrive unauthenticated and
 * the browser would cheerfully save the 401 body under a .xlsx name. Fetching
 * with the token and saving the blob is the only way the file is the file.
 *
 * The filename comes from Content-Disposition so the server stays the single
 * authority on naming — it already knows the shop and the range.
 */
export async function downloadReportExport(
  report: ExportableReport,
  format: ExportFormat,
  range: ReportRange,
): Promise<void> {
  const token = tenantSession.getAccessToken();
  if (token === null) throw new Error('Your session has expired. Sign in again.');

  const response = await fetch(
    `${API_URL}/portal/reports/${report}/export.${format}${rangeQuery(range)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  if (!response.ok) {
    // The error body is JSON even though the request asked for a file.
    let detail = `Export failed (${response.status}).`;
    try {
      const body = (await response.json()) as { message?: string | string[] };
      const message = Array.isArray(body.message) ? body.message.join('; ') : body.message;
      if (message) detail = message;
    } catch {
      // Not JSON — a proxy error page. The status is all we can say.
    }
    throw new Error(detail);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filenameFrom(response.headers.get('content-disposition'))
    ?? `${report}.${format}`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next tick: revoking synchronously can cancel the download in
  // some browsers before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `attachment; filename="sales-report_shop_2026-07-01_2026-07-31.xlsx"` */
function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : null;
}

export function fetchCustomers(signal?: AbortSignal): Promise<CustomerListReport> {
  return portalApi.get<CustomerListReport>('/portal/customers', { signal });
}

export function fetchCustomer(
  key: string,
  signal?: AbortSignal,
): Promise<CustomerDetail> {
  // The key can contain ':' (d:<device>:<id>), so it must be encoded.
  return portalApi.get<CustomerDetail>(
    `/portal/customers/${encodeURIComponent(key)}`,
    { signal },
  );
}

/**
 * The dashboard's "new customers today" and "returning customers today" lists.
 * [date] is the tenant's local day (`filters.today`), so the browser's own
 * timezone never decides which day this is.
 */
export function fetchCustomersToday(
  date: string,
  signal?: AbortSignal,
): Promise<CustomersTodayReport> {
  return portalApi.get<CustomersTodayReport>(
    `/portal/dashboard/customers-today${queryString({ date })}`,
    { signal },
  );
}
